import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from '../../components/ui/PageHeader';
import usePageTitle from '../../hooks/usePageTitle';
import { userService, User } from '../../services/userService';
import { useSnackbar } from 'notistack';

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function bytesToGbDisplay(bytes: number): string {
  return (bytes / 1024 ** 3).toFixed(3).replace(/\.?0+$/, '');
}

const UserStorageQuotas: React.FC = () => {
  usePageTitle('User storage quotas');
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<User[]>([]);
  const [tenantCapBytes, setTenantCapBytes] = useState<number | null>(null);
  const [tenantAllocatedBytes, setTenantAllocatedBytes] = useState<number>(0);
  const [tenantAllocatableRemainingBytes, setTenantAllocatableRemainingBytes] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [quotaGb, setQuotaGb] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getUsersWithStorageSummary();
      setRows(data.users || []);
      const cap = data.tenantCapBytes;
      const capN = cap != null ? Number(cap) : NaN;
      setTenantCapBytes(Number.isFinite(capN) ? capN : null);
      const allocN = Number(data.tenantAllocatedBytes);
      setTenantAllocatedBytes(Number.isFinite(allocN) ? allocN : 0);
      const rem = data.tenantAllocatableRemainingBytes;
      const remN = rem != null ? Number(rem) : NaN;
      setTenantAllocatableRemainingBytes(Number.isFinite(remN) ? remN : null);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load users';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (u: User) => {
    setEditUser(u);
    const m = u.membershipStorageQuotaBytes;
    if (m != null && m > 0) {
      setQuotaGb(bytesToGbDisplay(m));
    } else {
      setQuotaGb('');
    }
  };

  const closeEdit = () => {
    setEditUser(null);
    setQuotaGb('');
  };

  const sumOtherExplicitAllocations = (excludeUserId: string): number =>
    rows.reduce((sum, u) => {
      if (u.id === excludeUserId) return sum;
      const q = u.allocatedStorageQuotaBytes;
      return sum + (q != null && q > 0 ? q : 0);
    }, 0);

  const maxAssignableBytesForUser = (userId: string): number | null => {
    if (tenantCapBytes == null || !Number.isFinite(tenantCapBytes)) return null;
    const others = sumOtherExplicitAllocations(userId);
    return Math.max(0, tenantCapBytes - others);
  };

  const saveQuota = async () => {
    if (!editUser) return;
    try {
      setSaving(true);
      if (quotaGb.trim() === '') {
        await userService.updateUserTenantStorageQuota(editUser.id, null);
        enqueueSnackbar('Tenant storage override cleared; defaults apply.', { variant: 'success' });
      } else {
        const gb = Number(quotaGb);
        if (!Number.isFinite(gb) || gb <= 0) {
          enqueueSnackbar('Enter a positive size in GB, or leave empty to clear.', {
            variant: 'warning',
          });
          return;
        }
        const bytes = Math.round(gb * 1024 ** 3);
        const cap = tenantCapBytes != null ? Number(tenantCapBytes) : null;
        if (cap != null && Number.isFinite(cap) && bytes > cap) {
          enqueueSnackbar(
            `Quota cannot exceed organization limit (${formatBytes(cap)} / ${bytesToGbDisplay(cap)} GB).`,
            { variant: 'error' }
          );
          return;
        }
        const maxAssignable = maxAssignableBytesForUser(editUser.id);
        if (maxAssignable != null && bytes > maxAssignable && bytes > (editUser.allocatedStorageQuotaBytes || 0)) {
          enqueueSnackbar(
            `Total assigned quotas cannot exceed organization limit. At most ${formatBytes(maxAssignable)} (${bytesToGbDisplay(maxAssignable)} GB) remains for this user.`,
            { variant: 'error' }
          );
          return;
        }
        const used = editUser.storageUsedBytes ?? 0;
        if (bytes < used) {
          enqueueSnackbar(`Quota cannot be below current usage (${formatBytes(used)}).`, {
            variant: 'error',
          });
          return;
        }
        await userService.updateUserTenantStorageQuota(editUser.id, bytes);
        enqueueSnackbar('Storage quota updated.', { variant: 'success' });
      }
      closeEdit();
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="User storage quotas"
        description={
          tenantCapBytes != null
            ? `Organization limit: ${formatBytes(tenantCapBytes)}. Explicit user allocations: ${formatBytes(tenantAllocatedBytes)}${
                tenantAllocatableRemainingBytes != null
                  ? ` (${formatBytes(tenantAllocatableRemainingBytes)} available for new allocations).`
                  : '.'
              }`
            : 'Explicit per-user caps; users without a cap share the organization pool.'
        }
        actions={
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => load()} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tenantCapBytes != null && tenantAllocatedBytes > tenantCapBytes && <Alert severity="warning" sx={{mb: 2}}>
        Existing user allocations exceed the organization limit by {formatBytes(tenantAllocatedBytes - tenantCapBytes)}.
        Reduce the allocations; additional increases are blocked by the server.
      </Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Organization storage limit applies to total data stored by all users. Per-user caps
        cannot exceed that ceiling.
      </Typography>

      <TableContainer component={Paper}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell align="right">Used</TableCell>
              <TableCell align="right">Effective limit</TableCell>
              <TableCell align="right">Tenant override</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((u) => {
              const displayName =
                [u.firstName, u.lastName].filter(Boolean).join(' ') || u.name || u.email;
              return (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>{displayName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {u.email}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatBytes(u.storageUsedBytes ?? 0)}</TableCell>
                  <TableCell align="right">
                    {formatBytes(u.effectiveStorageLimitBytes ?? 0)}
                  </TableCell>
                  <TableCell align="right">
                    {u.membershipStorageQuotaBytes != null && u.membershipStorageQuotaBytes > 0
                      ? formatBytes(u.membershipStorageQuotaBytes)
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit quota">
                      <IconButton size="small" onClick={() => openEdit(u)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(editUser)} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Storage quota — {editUser ? editUser.email : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Leave empty and save to clear the per-user override. Assigned caps from all users
            combined cannot exceed the organization limit.
            {editUser && tenantCapBytes != null && (
              <>
                {' '}
                For this user, at most{' '}
                {formatBytes(maxAssignableBytesForUser(editUser.id) ?? tenantCapBytes)} (
                {bytesToGbDisplay(maxAssignableBytesForUser(editUser.id) ?? tenantCapBytes)} GB)
                can be assigned.
              </>
            )}
          </Typography>
          <TextField
            fullWidth
            label="Storage limit (GB)"
            value={quotaGb}
            onChange={(e) => setQuotaGb(e.target.value)}
            type="number"
            inputProps={{
              min: 0,
              step: 0.001,
              max: (() => {
                if (tenantCapBytes == null || !Number.isFinite(tenantCapBytes)) return undefined;
                const maxB =
                  editUser != null
                    ? maxAssignableBytesForUser(editUser.id) ?? tenantCapBytes
                    : tenantCapBytes;
                return Number(bytesToGbDisplay(maxB));
              })(),
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">GB</InputAdornment>,
            }}
            helperText={
              editUser && tenantCapBytes != null
                ? `Up to ${bytesToGbDisplay(maxAssignableBytesForUser(editUser.id) ?? tenantCapBytes)} GB for this user (${bytesToGbDisplay(tenantCapBytes)} GB org limit)`
                : tenantCapBytes != null
                  ? `Maximum ${bytesToGbDisplay(tenantCapBytes)} GB for this organization`
                  : undefined
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={saveQuota} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserStorageQuotas;
